import _extends from "@babel/runtime/helpers/esm/extends";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable no-constant-condition */
import { useState, useEffect, useRef, useMemo } from 'react';
import { components } from 'react-select';
import { Checkbox, Icon, ReactSelect } from '..';
import { Components } from './multiSelect.style';
import { ClearIndicator } from '../ReactSelect/CommonComponents/CommonComponents';
import Default from './components/Options/Default';
import { checkIfSubOptionsChecked, checkIsAllDeselected, checkIsAllSelected, deSelectAllOption, heighLighter, modifyMultiSelectValue, noHeighLighter, selectAllOption, getAllGroupHeadingIds, modifyOptionsWithDisabled, modifySelectedValues, getAllOptions } from './MultiSelect.utils';
import MultiLevel from './components/Options/MultiLevel';
const MultiSelect = _a => {
  var {
      className = 'multiSelect',
      disabled,
      fit = 'medium',
      limitTags = 0,
      options = [],
      renderValue = 'label',
      value = [],
      variant = 'default',
      onChange,
      hasSelectAll = true,
      isLoading = false,
      expandedItemIds,
      isAllExpanded = expandedItemIds ? false : true,
      onExpand,
      loadingOptions = [],
      isGroupExpandable = true,
      isGroupAllSelectable = true
    } = _a,
    rest = __rest(_a, ["className", "disabled", "fit", "limitTags", "options", "renderValue", "value", "variant", "onChange", "hasSelectAll", "isLoading", "expandedItemIds", "isAllExpanded", "onExpand", "loadingOptions", "isGroupExpandable", "isGroupAllSelectable"]);
  const valueRef = useRef();
  const optionsRef = useRef();
  const [optionSelected, setOptionSelected] = useState(value);
  const [localOptions, setLocalOptions] = useState(options);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [expandedList, setExpandedList] = useState(isAllExpanded ? getAllGroupHeadingIds(localOptions) : expandedItemIds ? expandedItemIds : []);
  const [rootLevelElements, setRootLevelELements] = useState([]);
  // logic for swapping label with value
  const swap = option => {
    const tmp = option.label;
    option.label = option.value;
    option.value = tmp;
  };
  // Adding selectAll checkbox if hasSelectAll is true
  useEffect(() => {
    if (hasSelectAll) {
      setLocalOptions(modifyOptionsWithDisabled([selectAllOption].concat(_toConsumableArray(options))));
    } else {
      setRootLevelELements(options.map(item => item.value));
      setLocalOptions(modifyOptionsWithDisabled(_toConsumableArray(options)));
    }
  }, [options, hasSelectAll]);
  // Adding deselectAll as label if allSelected in case variant is default
  useEffect(() => {
    if (hasSelectAll && variant === 'default') {
      if (selectAllChecked) {
        setLocalOptions([deSelectAllOption].concat(_toConsumableArray(options)));
      } else {
        setLocalOptions([selectAllOption].concat(_toConsumableArray(options)));
      }
    }
  }, [selectAllChecked]);
  // Called if renderValue is value instead of label
  useEffect(() => {
    if (renderValue !== 'label') {
      const iterator = options => {
        (options || []).forEach(option => {
          if (option.options) {
            iterator(option.options);
          } else {
            swap(option);
          }
        });
      };
      iterator(localOptions);
    }
  }, [renderValue]);
  useEffect(() => {
    var _a, _b;
    const options = _toConsumableArray(localOptions);
    if (options && (((_a = options[0]) === null || _a === void 0 ? void 0 : _a.value) === 'selectAll' || ((_b = options[0]) === null || _b === void 0 ? void 0 : _b.value) === 'deselectAll')) {
      (options || []).splice(0, 1);
    }
    if (JSON.stringify(valueRef.current) !== JSON.stringify(value) || JSON.stringify(optionsRef.current) !== JSON.stringify(options)) {
      const modifiedValues = modifySelectedValues(localOptions, value);
      setOptionSelected(modifiedValues);
      valueRef.current = value;
      optionsRef.current = options;
      if (!value.length) {
        setSelectAllChecked(false);
      }
      // if (typeof onChange === 'function') {
      //   onChange(modifiedValues, undefined);
      // }
    }
  }, [value, localOptions]);
  const handleChange = (newValue, event) => {
    let isAllSelected,
      isAllDeselected,
      modifiedValue = [];
    if (variant === 'default' || variant === 'vanity') {
      isAllSelected = hasSelectAll ? checkIsAllSelected(newValue) : false;
      isAllDeselected = hasSelectAll ? checkIsAllDeselected(newValue) : false;
      modifiedValue = modifyMultiSelectValue(newValue, localOptions, isAllSelected, isAllDeselected);
    }
    if (['checkbox', 'svg', 'avatar', 'group', 'multi-level', 'spread'].includes(variant)) {
      isAllSelected = hasSelectAll ? checkIsAllSelected(newValue) && !selectAllChecked : false;
      isAllDeselected = hasSelectAll ? checkIsAllSelected(newValue) && selectAllChecked : false;
      modifiedValue = modifyMultiSelectValue(newValue, localOptions, isAllSelected, isAllDeselected);
    }
    if (isAllSelected) {
      setSelectAllChecked(true);
    } else if (isAllDeselected) {
      setSelectAllChecked(false);
    } else if (hasSelectAll) {
      setSelectAllChecked(false);
    }
    if (['multi-level', 'spread', 'group'].includes(variant) && modifiedValue.length === getAllOptions(localOptions).filter(item => item.value !== 'selectAll').length) {
      setSelectAllChecked(true);
    } else if (['checkbox', 'avatar', 'default', 'svg', 'vanity'].includes(variant) && modifiedValue.length === localOptions.length - 1) {
      setSelectAllChecked(true);
    }
    setOptionSelected(modifiedValue);
    if (typeof onChange === 'function') {
      onChange(modifiedValue, event);
    }
  };
  const MultiValue = props => {
    if (limitTags) {
      const maxToShow = Number(limitTags);
      const overflow = optionSelected.slice(maxToShow).map(x => x.label);
      return props.index < maxToShow ? _jsx(components.MultiValue, _extends({}, props)) : props.index === maxToShow ? _jsx(Components.LimitTagBadge, {
        className: "removeLabel",
        appearance: "primitive",
        value: "+ ".concat(overflow.length)
      }) : null;
    } else {
      return _jsx(components.MultiValue, _extends({}, props));
    }
  };
  const handleGroupHeadingChange = (e, data) => {
    const result = [];
    const resultValues = [];
    if (!data.options) {
      result.push(data);
      resultValues.push(data.value);
    }
    const handleNestedOptions = nestedData => {
      (nestedData.options || []).forEach(item => {
        if (item.options) {
          handleNestedOptions(item);
        } else {
          if (!item.isDisabled) {
            result.push(item);
            resultValues.push(item.value);
          }
        }
      });
    };
    handleNestedOptions(data);
    let modifiedValue;
    const optionSelectedValues = optionSelected.map(item => item.value);
    if (e.target.checked) {
      const newValues = result.filter(item => !optionSelectedValues.includes(item.value));
      modifiedValue = [].concat(_toConsumableArray(optionSelected), _toConsumableArray(newValues));
      setOptionSelected(modifiedValue);
    } else {
      const oldValues = optionSelected.filter(item => !resultValues.includes(item.value));
      modifiedValue = _toConsumableArray(oldValues);
      setOptionSelected(modifiedValue);
    }
    if (['multi-level', 'spread', 'group'].includes(variant) && modifiedValue.length === getAllOptions(localOptions).filter(item => item.value !== 'selectAll').length) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
    if (typeof onChange === 'function') {
      onChange(modifiedValue, e);
    }
  };
  const handleExpandedList = (data, expanded) => {
    const {
      value
    } = data;
    if (typeof onExpand === 'function') {
      onExpand(data, expanded);
    }
    const index = expandedList.indexOf(value);
    if (index > -1) {
      const arr = _toConsumableArray(expandedList);
      arr.splice(index, 1);
      setExpandedList(arr);
    } else {
      setExpandedList([].concat(_toConsumableArray(expandedList), [value]));
    }
  };
  const GroupHeading = props => {
    const {
      allChecked,
      anyChecked
    } = useMemo(() => checkIfSubOptionsChecked(props.data, optionSelected), [optionSelected]);
    return _jsxs(components.GroupHeading, _extends({}, props, {
      isGroupAllSelectable: isGroupAllSelectable,
      isGroupExpandable: isGroupExpandable,
      className: "".concat(expandedList.includes(props.data.value) ? '' : 'collapsed', " ").concat(rootLevelElements.includes(props.data.value) ? 'rootElement' : '', " ").concat(!isGroupExpandable ? 'isGroupNotExpandable' : '')
    }, {
      children: [isGroupExpandable && props.data.hasChildren && _jsx(_Fragment, {
        children: expandedList.includes(props.data.value) ? _jsx(Icon, {
          name: 'caret-down',
          onClick: () => handleExpandedList(props.data, false)
        }) : _jsx(Icon, {
          name: 'caret-up',
          onClick: () => handleExpandedList(props.data, true)
        })
      }), isGroupAllSelectable ? _jsx(Checkbox, {
        checked: allChecked || anyChecked,
        indeterminate: anyChecked && !allChecked,
        id: props.data.value,
        name: props.data.value,
        disabled: props.data.isDisabled,
        fit: fit,
        onChange: e => handleGroupHeadingChange(e, props.data),
        label: props.data.label
      }) : _jsx(_Fragment, {
        children: props.data.label
      })]
    }));
  };
  const Option = props => {
    return _jsxs(_Fragment, {
      children: [(variant === 'checkbox' || variant === 'avatar' || variant === 'default' || variant === 'svg' || variant === 'vanity') && _jsx(Default, _extends({
        selectAllChecked: selectAllChecked,
        variant: variant,
        fit: fit
      }, props)), (variant === 'multi-level' || variant === 'spread' || variant === 'group') && _jsx(Components.GroupOption, _extends({
        className: props.value,
        variant: variant,
        value: props.value,
        isRootElement: rootLevelElements.includes(props.value)
      }, {
        children: props.data.options ? _jsx(MultiLevel, _extends({
          currOption: props.data,
          nestedOptions: props.data.options,
          fit: fit,
          expandedList: expandedList,
          handleGroupHeadingChange: handleGroupHeadingChange,
          handleExpandedList: handleExpandedList,
          loadingOptions: loadingOptions,
          optionSelected: optionSelected
        }, props)) : typeof props.data.hasChildren !== 'undefined' ? _jsxs(_Fragment, {
          children: [_jsx(GroupHeading, _extends({}, props)), loadingOptions.includes(props.value) && _jsx(Components.Loading, {
            label: "loading",
            orientation: "horizontal"
          })]
        }) : _jsx(components.Option, _extends({}, props, {
          children: _jsx(Checkbox, {
            checked: props.value === 'selectAll' ? selectAllChecked || optionSelected.length : props.isSelected,
            indeterminate: !!(props.value === 'selectAll' && optionSelected.length && !selectAllChecked),
            id: props.data.value,
            name: props.data.value,
            disabled: props.data.isDisabled,
            fit: fit,
            onChange: () => null,
            label: props.data.label
          })
        }))
      }))]
    });
  };
  const customCreateOption = label => {
    return {
      isChecked: false,
      isIndeterminate: false,
      render: true,
      id: label.toLowerCase().replace(/\W/g, ''),
      label: label.toLowerCase().replace(/\W/g, ''),
      value: label.toLowerCase().replace(/\W/g, '')
    };
  };
  const CustomClearIndicator = props => {
    return _jsx(ClearIndicator, _extends({}, props, {
      fit: fit,
      disabled: disabled
    }));
  };
  return _jsx(Components.Multi.OptionsWrapper, _extends({
    className: "".concat(className, " ").concat(variant)
  }, {
    children: _jsx(ReactSelect, _extends({}, rest, {
      disabled: disabled,
      styles: variant === 'default' ? heighLighter : noHeighLighter,
      hideSelectedOptions: false,
      value: optionSelected,
      components: {
        Option,
        MultiValue,
        ClearIndicator: CustomClearIndicator,
        GroupHeading
      },
      variant: variant === 'vanity' ? 'vanity' : 'default',
      options: localOptions,
      fit: fit,
      renderValue: renderValue,
      onChange: handleChange,
      isMulti: true,
      closeMenuOnSelect: false,
      customCreateOption: customCreateOption,
      isLoading: disabled ? false : isLoading
    }))
  }));
};
export default MultiSelect;